import { noOp } from 'common/Constants';
import { ClientEvents } from 'common/events/ClientEvents';
import React, { useContext, useEffect } from 'react';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useGetSearchData } from '../../../store/hooks/useGetSearch';
import { useSearchQueryState } from '../../../store/hooks/useSearchQuery';
import { SearchDataContext } from '../../../store/SearchDataContext';
import { NoResultsImage } from '../NoResults';
import { CuisineCard, DishCard, LoadingCuisineCard, LoadingDishCard, LoadingShefCard, ShefCard } from './Card';
import { NO_RESULTS_TEXT, SECTION_TEXT, VIEW_RESULTS_TEXT } from './consts';
import {
  Container,
  LoadingContainer,
  NoResultsContainer,
  NoResultsText,
  Section,
  SectionHeader,
  SectionLoading,
  SectionResults,
  ViewResultsButton,
  ViewResultsContainer,
  Wrapper,
} from './styles';

export interface PartialStateProps {
  onClickViewAll?: () => void;
}

export const PartialStateResults: React.FC<PartialStateProps> = ({ onClickViewAll = noOp }) => {
  const { data, activeZipCode, loading } = useContext(SearchDataContext);
  const {
    previewShefResults,
    previewDishResults,
    previewCuisineResults,
    shefResultIds,
    dishResultIds,
    cuisineResultIds,
  } = useGetSearchData(data);
  const query = useSearchQueryState();

  const hasCuisineResults = !!previewCuisineResults.length;
  const hasShefResults = !!previewShefResults.length;
  const hasDishResults = !!previewDishResults.length;
  const hasResults = hasCuisineResults || hasShefResults || hasDishResults;

  const tracker = useTracker();

  useEffect(() => {
    tracker.track(ClientEvents.FOOD_SEARCH_RESULTS_DISPLAYED, {
      query,
      shef_results: shefResultIds,
      food_item_results: dishResultIds,
      cuisine_results: cuisineResultIds,
    });
  }, [tracker, query, shefResultIds, dishResultIds, cuisineResultIds]);

  if (loading || !data) {
    return (
      <LoadingContainer>
        <SectionLoading>
          <SectionResults>
            <LoadingCuisineCard />
            <LoadingCuisineCard />
            <LoadingCuisineCard />
          </SectionResults>
        </SectionLoading>
        <SectionLoading>
          <SectionResults>
            <LoadingShefCard />
            <LoadingShefCard />
            <LoadingShefCard />
          </SectionResults>
        </SectionLoading>
        <SectionLoading>
          <SectionResults>
            <LoadingDishCard />
            <LoadingDishCard />
            <LoadingDishCard />
          </SectionResults>
        </SectionLoading>
      </LoadingContainer>
    );
  }

  return hasResults ? (
    <Container>
      <Wrapper>
        {hasCuisineResults && (
          <Section>
            <SectionHeader>{SECTION_TEXT.CUISINES}</SectionHeader>
            <SectionResults>
              {previewCuisineResults.map((cuisineResult, idx) => (
                <CuisineCard
                  cuisineResult={cuisineResult}
                  query={query}
                  zipCode={activeZipCode}
                  key={`cuisine-${idx}`}
                  hideCounts
                />
              ))}
            </SectionResults>
          </Section>
        )}
        {hasShefResults && (
          <Section>
            <SectionHeader>{SECTION_TEXT.SHEFS}</SectionHeader>
            <SectionResults>
              {previewShefResults.map(({ shef }) => (
                <ShefCard shefResult={shef} query={query} key={`shef-${shef.id}`} />
              ))}
            </SectionResults>
          </Section>
        )}
        {hasDishResults && (
          <Section>
            <SectionHeader>{SECTION_TEXT.DISHES}</SectionHeader>
            <SectionResults>
              {previewDishResults.map((dishResult) => (
                <DishCard dishResult={dishResult} query={query} key={`dish-${dishResult.id}`} />
              ))}
            </SectionResults>
          </Section>
        )}
      </Wrapper>
      <ViewResultsContainer>
        <ViewResultsButton
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            onClickViewAll();
          }}>
          {VIEW_RESULTS_TEXT}
        </ViewResultsButton>
      </ViewResultsContainer>
    </Container>
  ) : (
    <NoResultsContainer>
      <NoResultsImage />
      <NoResultsText>
        <div>{NO_RESULTS_TEXT}</div>
        <div>"{query}"</div>
      </NoResultsText>
    </NoResultsContainer>
  );
};
